import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  Eye,
  Filter,
  LucideAngularModule,
  Download,
  ReceiptText,
  Send,
  Search,
  Wallet,
} from 'lucide-angular';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Contract, Loa, LoaBillConfig, Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { ContractsService } from '../../contract/services/contracts.service';
import { LoaService } from '../../loa/services/loa.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import { ToastService } from '../../../shared/ui/toast.service';

type BillMode = 'sop' | 'tmr';
type BillStatus = 'Pending' | 'Generated' | 'Approved';

interface BillRow {
  rowKey: string;
  groupKey?: string;
  applicationId: string;
  applicationNo: string;
  applicationLink: string;
  applicationCount: number;
  applicationNumbers: string[];
  billId: string;
  loiId: string;
  contractId: string;
  loiRef: string;
  sellerVendorId: string;
  seller: string;
  buyer: string;
  buyerAddress: string;
  period: string;
  periodFrom: string;
  periodTo: string;
  billCycle: string;
  scheduled: number;
  billedQuantity: number;
  rldcActual: number | null;
  rate: number;
  netPayable: number;
  status: BillStatus;
  mode: BillMode;
  canGenerate: boolean;
  approvalStatus: string;
  approvalAvailable: boolean;
  scheduledTooltipLines: string[];
  rldcTooltipLines: string[];
  calculationTooltipLines: string[];
}

interface ApplicationScheduleApi {
  from_date: string;
  to_date: string;
  from_time: string;
  to_time: string;
  quantum?: number | null;
}

interface ApplicationApi {
  id: string;
  application_number: string;
  contract_id: string;
  seller_vendor_id?: string | null;
  buyer_vendor_id?: string | null;
  seller_loa_id?: string | null;
  buyer_loa_id?: string | null;
  total_quantum?: number | null;
  approved_quantum?: number | null;
  status: string;
  schedules?: ApplicationScheduleApi[];
  approval_status?: string;
  approval_available?: boolean;
  approval_schedules?: ApplicationApprovalScheduleApi[];
}

interface ApplicationApprovalScheduleApi {
  schedule_from: string;
  schedule_to?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  mwh?: number | null;
  requested_mw?: number | null;
  accepted_mw?: number | null;
  is_approved?: boolean;
}

interface GeneratedInvoiceApi {
  row_key: string;
  bill_id: string;
  mode?: string | null;
  invoice_type?: string | null;
  bill_for?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  rebate_date?: string | null;
  rebate_percentage?: number | null;
  row_json?: Record<string, unknown> | null;
}

interface McpReportApi {
  id: string;
}

interface McpReportDetailApi {
  id: string;
  rows?: McpReportRowApi[];
}

interface McpReportRowApi {
  report_date: string;
  period: string;
  mcp?: number | null;
}

type InvoiceLineItem = {
  description: string;
  hsnSac: string;
  quantity: string;
  rate: string;
  per: string;
  amount: number;
};

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './sop-tmr-billing.page.html',
  styleUrl: './sop-tmr-billing.page.scss',
})
export class SopTmrBillingPage implements OnInit {
  protected readonly issuerProfile = {
    companyName: 'SHARAJMAN TECHNOLOGY',
    address: '-',
    pan: 'AACCE4669H',
    gst: '29AACCE4669H1Z6',
    state: '-',
    stateCode: '-',
    cin: '-',
  } as const;
  protected readonly bankProfile = {
    accountHolderName: 'SHARAJMAN TECHNOLOGY',
    bankName: '-',
    accountNumber: '-',
    branch: '-',
    ifscCode: '-',
    swiftCode: '-',
  } as const;
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly contractsService = inject(ContractsService);
  private readonly vendorsService = inject(VendorsService);
  private readonly loaService = inject(LoaService);
  private readonly toast = inject(ToastService);

  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly FilterIcon = Filter;
  protected readonly DownloadIcon = Download;
  protected readonly SearchIcon = Search;
  protected readonly EyeIcon = Eye;
  protected readonly SendIcon = Send;
  protected readonly WalletIcon = Wallet;
  protected readonly BarChart3Icon = BarChart3;
  protected readonly ReceiptTextIcon = ReceiptText;

  protected readonly mode = signal<BillMode>('sop');
  protected readonly showFilters = signal(false);
  protected readonly search = signal('');
  protected readonly clientFilter = signal('');
  protected readonly draftStatus = signal<'all' | 'pending' | 'generated' | 'approved'>('all');
  protected readonly draftFromDate = signal('');
  protected readonly draftToDate = signal('');
  protected readonly appliedStatus = signal<'all' | 'pending' | 'generated' | 'approved'>('all');
  protected readonly appliedFromDate = signal('');
  protected readonly appliedToDate = signal('');
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly pageSizes = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly loading = signal(true);

  private readonly applications = signal<ApplicationApi[]>([]);
  private readonly contracts = signal<Contract[]>([]);
  private readonly vendors = signal<Vendor[]>([]);
  private readonly loas = signal<Loa[]>([]);
  private readonly billConfigsByLoa = signal<Record<string, LoaBillConfig[]>>({});
  private readonly generatedBillIds = signal<Record<string, string>>({});
  private readonly latestMcpRows = signal<McpReportRowApi[]>([]);
  private readonly selectedRows = signal<Record<string, boolean>>({});
  protected readonly showPreviewModal = signal(false);
  protected readonly previewInvoiceRow = signal<BillRow | null>(null);
  protected readonly showGenerateConfirmModal = signal(false);
  protected readonly generateConfirmRow = signal<BillRow | null>(null);
  private pendingLoadTasks = 0;
  private mainLoadComplete = false;

  protected readonly rows = computed<BillRow[]>(() => {
    const apps = this.applications();
    const contracts = new Map(this.contracts().map((item) => [item.id, item]));
    const vendors = new Map(this.vendors().map((item) => [item.id, item]));
    const loas = new Map(this.loas().map((item) => [item.id, item]));
    const billConfigs = this.billConfigsByLoa();
    const generatedBillIds = this.generatedBillIds();
    const today = this.startOfDay(new Date());
    const yesterday = this.addDays(today, -1);

    const rows: BillRow[] = [];
    for (const app of apps) {
      const appStatus = (app.status || '').toLowerCase();
      if (!['approved', 'punched'].includes(appStatus)) continue;

      const contract = contracts.get(app.contract_id);
      const sellerVendor = app.seller_vendor_id ? vendors.get(app.seller_vendor_id) : null;
      const sellerName = sellerVendor?.vendor_name || '-';
      const billToVendorId = this.resolveBuyerVendorId(app, contract, loas) || app.buyer_vendor_id || app.seller_vendor_id || '';
      const billToVendor = billToVendorId ? vendors.get(billToVendorId) : null;
      const buyerName = billToVendor?.vendor_name || '-';
      const buyerAddress = billToVendor?.billing_address || billToVendor?.registered_address || '-';

      const modeRows: BillRow[] = [
        ...this.buildRowsForMode(
          'sop',
          app,
          contract,
          sellerName,
          buyerName,
          buyerAddress,
          [(app.application_number || '').trim()].filter(Boolean),
          loas,
          billConfigs,
          generatedBillIds,
          yesterday,
          today,
        ),
        ...this.buildRowsForMode(
          'tmr',
          app,
          contract,
          sellerName,
          buyerName,
          buyerAddress,
          [(app.application_number || '').trim()].filter(Boolean),
          loas,
          billConfigs,
          generatedBillIds,
          yesterday,
          today,
        ),
      ];

      rows.push(...modeRows);
    }

    return this.aggregateBillRows(rows);
  });

  protected readonly modeRows = computed(() => this.rows().filter((row) => row.mode === this.mode()));
  protected readonly clientOptions = computed(() => {
    const seen = new Set<string>();
    return this.modeRows()
      .filter((row) => !!row.sellerVendorId && !!row.seller)
      .filter((row) => {
        if (seen.has(row.sellerVendorId)) return false;
        seen.add(row.sellerVendorId);
        return true;
      })
      .map((row) => ({ id: row.sellerVendorId, name: row.seller }))
      .sort((left, right) => left.name.localeCompare(right.name));
  });
  protected readonly filteredRows = computed(() => {
    const clientId = this.clientFilter().trim();
    const status = this.appliedStatus();
    const fromDate = this.parseIsoDate(this.appliedFromDate());
    const toDate = this.parseIsoDate(this.appliedToDate());
    const term = this.search().trim().toLowerCase();
    return this.modeRows().filter((row) => {
      if (clientId && row.sellerVendorId !== clientId) return false;
      if (status !== 'all' && row.status.toLowerCase() !== status) return false;
      const rowFrom = this.parsePeriodDisplayDate(this.periodStartDateLabel(row));
      const rowTo = this.parsePeriodDisplayDate(this.periodEndDateLabel(row));
      if (fromDate && rowTo && rowTo < fromDate) return false;
      if (toDate && rowFrom && rowFrom > toDate) return false;
      if (!term) return true;
      const text = [row.billId, row.loiId, row.contractId, row.loiRef, row.buyer, row.period, row.status].join(' ').toLowerCase();
      return text.includes(term);
    });
  });
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  protected readonly currentPage = computed(() => Math.min(this.page(), this.totalPages()));
  protected readonly pagedRows = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  protected readonly startRow = computed(() => (this.filteredRows().length === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  protected readonly endRow = computed(() => Math.min(this.currentPage() * this.pageSize(), this.filteredRows().length));

  protected readonly totalSop = computed(() =>
    this.rows()
      .filter((row) => row.mode === 'sop')
      .reduce((sum, row) => sum + row.netPayable, 0),
  );

  protected readonly totalTmr = computed(() =>
    this.rows()
      .filter((row) => row.mode === 'tmr')
      .reduce((sum, row) => sum + row.netPayable, 0),
  );

  protected readonly sopBillCount = computed(() => this.rows().filter((row) => row.mode === 'sop').length);
  protected readonly tmrBillCount = computed(() => this.rows().filter((row) => row.mode === 'tmr').length);
  protected readonly generatedCount = computed(() => this.modeRows().filter((row) => row.status === 'Generated').length);
  protected readonly pendingCount = computed(() => this.modeRows().filter((row) => row.status === 'Pending').length);
  protected readonly approvalGapCount = computed(() => this.modeRows().filter((row) => !row.approvalAvailable).length);
  protected readonly selectedCount = computed(() => this.filteredRows().filter((row) => this.isSelected(row.rowKey)).length);
  protected readonly sentCount = computed(() => 0);
  protected readonly invoiceIssueDate = computed(() => this.formatDisplayDate(new Date()));
  protected readonly invoiceDueDate = computed(() => this.formatNumericDate(this.addDays(new Date(), 7)));
  protected readonly invoiceFinancialYear = computed(() => this.currentFinancialYear(new Date()));

  protected relatedApplicationNumbers(row: BillRow): string[] {
    if (row.applicationNumbers.length) return [...row.applicationNumbers].sort((left, right) => left.localeCompare(right));
    if (!row.billId) return row.applicationNo ? [row.applicationNo] : [];
    return Array.from(
      new Set(
        this.rows()
          .filter((item) => item.mode === row.mode && item.billId === row.billId)
          .map((item) => (item.applicationNo || '').trim())
          .filter((value) => !!value),
      ),
    );
  }

  private aggregateBillRows(rows: BillRow[]): BillRow[] {
    const grouped = new Map<string, BillRow>();

    for (const row of rows) {
      const groupKey = [
        row.mode,
        row.loiId,
        row.contractId,
        row.seller,
        row.buyer,
        row.billCycle,
        row.groupKey || row.period,
        row.rate.toFixed(4),
      ].join('::');

      const existing = grouped.get(groupKey);
      if (!existing) {
        const initialApplicationNumbers = Array.from(
          new Set(row.applicationNumbers.length ? row.applicationNumbers : [row.applicationNo].filter(Boolean)),
        ).sort((left, right) => left.localeCompare(right));
        grouped.set(groupKey, {
          ...row,
          applicationNo: initialApplicationNumbers[0] || row.applicationNo,
          applicationNumbers: initialApplicationNumbers,
          applicationCount: initialApplicationNumbers.length,
        });
        continue;
      }

      const mergedApplicationNumbers = Array.from(
        new Set([...existing.applicationNumbers, ...row.applicationNumbers, existing.applicationNo, row.applicationNo].filter(Boolean)),
      ).sort((left, right) => left.localeCompare(right));
      const resolvedBillId =
        existing.billId && row.billId && existing.billId === row.billId
          ? existing.billId
          : existing.billId || row.billId || '';

      grouped.set(groupKey, {
        ...existing,
        rowKey: [existing.rowKey, row.rowKey].filter(Boolean).join('||'),
        applicationId: [existing.applicationId, row.applicationId].filter(Boolean).join(','),
        applicationNo: mergedApplicationNumbers[0] || existing.applicationNo,
        applicationCount: mergedApplicationNumbers.length,
        applicationNumbers: mergedApplicationNumbers,
        period: this.mergeDisplayPeriod(existing.period, row.period),
        billId: resolvedBillId,
        scheduled: existing.scheduled + row.scheduled,
        billedQuantity: existing.billedQuantity + row.billedQuantity,
        rldcActual:
          existing.rldcActual === null && row.rldcActual === null
            ? null
            : (existing.rldcActual || 0) + (row.rldcActual || 0),
        netPayable: existing.netPayable + row.netPayable,
        status: resolvedBillId ? 'Generated' : existing.status === 'Generated' || row.status === 'Generated' ? 'Generated' : 'Pending',
        canGenerate: existing.canGenerate || row.canGenerate,
        approvalAvailable: existing.approvalAvailable || row.approvalAvailable,
        approvalStatus:
          existing.approvalAvailable || row.approvalAvailable
            ? 'Available'
            : existing.approvalStatus || row.approvalStatus,
        scheduledTooltipLines: [...existing.scheduledTooltipLines, ...row.scheduledTooltipLines],
        rldcTooltipLines: [...existing.rldcTooltipLines, ...row.rldcTooltipLines],
        calculationTooltipLines: [...existing.calculationTooltipLines, ...row.calculationTooltipLines],
      });
    }

    return Array.from(grouped.values());
  }

  ngOnInit(): void {
    this.loading.set(true);
    this.pendingLoadTasks = 0;
    this.mainLoadComplete = false;
    forkJoin({
      apps: this.api.get<ApplicationApi[]>('/api/applications').pipe(catchError(() => of([]))),
      contracts: this.contractsService.list().pipe(catchError(() => of([]))),
      vendors: this.vendorsService.list().pipe(catchError(() => of([]))),
      loas: this.loaService.list().pipe(catchError(() => of([]))),
      generated: this.api.get<GeneratedInvoiceApi[]>('/api/invoices/generated').pipe(catchError(() => of([]))),
      mcpReports: this.api.get<McpReportApi[]>('/api/mcp-reports').pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ apps, contracts, vendors, loas, generated, mcpReports }) => {
        this.applications.set(apps || []);
        this.contracts.set(contracts || []);
        this.vendors.set(vendors || []);
        this.loas.set(loas || []);
        const generatedMap = (generated || []).reduce<Record<string, string>>((acc, item) => {
          if (item?.row_key && item?.bill_id) acc[item.row_key] = item.bill_id;
          return acc;
        }, {});
        this.generatedBillIds.set(generatedMap);
        const latestMcpReportId = (mcpReports || [])[0]?.id;
        if (latestMcpReportId) {
          this.beginLoadTask();
          this.api
            .get<McpReportDetailApi>(`/api/mcp-reports/${latestMcpReportId}`)
            .pipe(catchError(() => of({ id: latestMcpReportId, rows: [] } as McpReportDetailApi)))
            .subscribe({
              next: (detail) => this.latestMcpRows.set(detail.rows || []),
              complete: () => this.endLoadTask(),
            });
        } else {
          this.latestMcpRows.set([]);
        }

        const loaIds = Array.from(
          new Set(
            (loas || [])
              .map((item) => item.id)
              .filter((id): id is string => typeof id === 'string' && !!id.trim()),
          ),
        );

        if (!loaIds.length) {
          this.billConfigsByLoa.set({});
        } else {
          this.beginLoadTask();
          const requests = loaIds.map((loaId) => this.loaService.listBillConfigs(loaId).pipe(catchError(() => of([] as LoaBillConfig[]))));
          forkJoin(requests).subscribe({
            next: (allConfigs) => {
              const mapped: Record<string, LoaBillConfig[]> = {};
              loaIds.forEach((id, index) => {
                mapped[id] = allConfigs[index] || [];
              });
              this.billConfigsByLoa.set(mapped);
            },
            complete: () => this.endLoadTask(),
          });
        }

        this.mainLoadComplete = true;
        this.finishLoadingIfReady();
      },
      error: () => {
        this.mainLoadComplete = true;
        this.pendingLoadTasks = 0;
        this.loading.set(false);
      },
    });
  }

  private beginLoadTask(): void {
    this.pendingLoadTasks += 1;
    this.loading.set(true);
  }

  private endLoadTask(): void {
    this.pendingLoadTasks = Math.max(0, this.pendingLoadTasks - 1);
    this.finishLoadingIfReady();
  }

  private finishLoadingIfReady(): void {
    if (!this.mainLoadComplete || this.pendingLoadTasks > 0) return;
    this.loading.set(false);
  }

  protected formatMoney(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value);
  }

  protected invoiceQty(value: number): string {
    return (Number(value) || 0).toFixed(2);
  }

  protected invoiceRate(value: number): string {
    return (Number(value) || 0).toFixed(4);
  }

  protected invoiceAmount(value: number): string {
    return (Number(value) || 0).toFixed(2);
  }

  protected invoiceWords(value: number): string {
    return `${this.numberToWordsIndian(Math.round(Number(value) || 0))} Rupees Only`;
  }

  protected invoiceFromDate(row: BillRow): string {
    return this.periodStartDateLabel(row);
  }

  protected invoiceToDate(row: BillRow): string {
    return this.periodEndDateLabel(row);
  }

  protected netPayableTooltipLines(row: BillRow): string[] {
    return [
      'Billing Qty = Sum of (days x hours x quantum x 250)',
      ...row.calculationTooltipLines,
      `Punched Scheduled Qty: ${this.formatMoney(row.scheduled)} kWh`,
      `Billable Qty: ${this.formatMoney(row.billedQuantity)} kWh`,
      `Rate: ${row.rate.toFixed(2)}`,
      `Net Payable = ${this.formatMoney(row.billedQuantity)} x ${row.rate.toFixed(2)} = ${this.formatMoney(row.netPayable)}`,
    ];
  }

  protected formatRldcActual(value: number | null): string {
    return value === null ? '-' : `${this.formatMoney(value)} kWh`;
  }

  protected requestGenerateBill(row: BillRow): void {
    if (!row.canGenerate || row.status === 'Generated') return;
    this.generateConfirmRow.set(row);
    this.showGenerateConfirmModal.set(true);
  }

  protected cancelGenerateBill(): void {
    this.showGenerateConfirmModal.set(false);
    this.generateConfirmRow.set(null);
  }

  protected confirmGenerateBill(): void {
    const row = this.generateConfirmRow();
    if (!row || !row.canGenerate || row.status === 'Generated') {
      this.cancelGenerateBill();
      return;
    }
    const nextBillId = this.nextBillId(row.mode);
    const generatedRow: BillRow = { ...row, billId: nextBillId, status: 'Generated' };
    this.generatedBillIds.update((current) => ({ ...current, [row.rowKey]: nextBillId }));
    this.selectedRows.update((current) => ({ ...current, [row.rowKey]: false }));
    this.persistGeneratedInvoices([
      {
        row_key: row.rowKey,
        bill_id: nextBillId,
        mode: row.mode,
        invoice_type: this.resolveInvoiceTypeFromTab(),
        bill_for: 'BUYER',
        row_json: this.buildPersistedRowJson(generatedRow, nextBillId),
      },
    ]);
    this.cancelGenerateBill();
    this.downloadGeneratedInvoice(generatedRow);
  }

  protected downloadGeneratedInvoice(row: BillRow): void {
    if (!row.billId) return;
    const payload = {
      bill_id: row.billId,
      mode: row.mode,
      seller: row.seller || '-',
      buyer: row.buyer || '-',
      buyer_address: row.buyerAddress || '-',
      application_no: row.applicationNo || '-',
      application_link:
        row.applicationLink && row.applicationLink.startsWith('/')
          ? `${window.location.origin}${row.applicationLink}`
          : row.applicationLink || '-',
      period: row.period || '-',
      scheduled: Number(row.billedQuantity) || 0,
      rate: Number(row.rate) || 0,
      amount: Number(row.netPayable) || 0,
    };
    this.http
      .post(this.api.resolve('/api/invoices/download-pdf'), payload, {
        withCredentials: true,
        responseType: 'blob',
      })
      .pipe(catchError(() => of(null)))
      .subscribe((blob) => {
        if (blob) {
          this.downloadBlob(blob, `${row.billId}.pdf`);
          return;
        }
        this.downloadInvoice(row);
        this.toast.show('PDF download unavailable', 'Downloaded invoice HTML instead.', 'info');
      });
  }

  protected viewInvoice(row: BillRow): void {
    const previewRow = row.billId ? row : { ...row, billId: this.nextBillId(row.mode) };
    this.previewInvoiceRow.set(previewRow);
    this.showPreviewModal.set(true);
  }

  protected closePreviewModal(): void {
    this.showPreviewModal.set(false);
    this.previewInvoiceRow.set(null);
  }

  protected applyToolbarFilters(): void {
    this.appliedStatus.set(this.draftStatus());
    this.appliedFromDate.set(this.draftFromDate());
    this.appliedToDate.set(this.draftToDate());
    this.page.set(1);
  }

  protected setDraftStatus(value: string): void {
    if (value === 'pending' || value === 'generated' || value === 'approved') {
      this.draftStatus.set(value);
      return;
    }
    this.draftStatus.set('all');
  }

  protected toggleFilters(): void {
    this.showFilters.update((current) => !current);
  }

  protected setMode(mode: BillMode): void {
    if (this.mode() === mode) return;
    this.mode.set(mode);
    this.page.set(1);
  }

  protected onSearch(value: string): void {
    this.search.set(value);
    this.page.set(1);
  }

  protected onClientFilterChange(value: string): void {
    this.clientFilter.set(value);
    this.page.set(1);
  }

  protected onDraftFromDateChange(value: string): void {
    this.draftFromDate.set(value);
    this.page.set(1);
  }

  protected onDraftToDateChange(value: string): void {
    this.draftToDate.set(value);
    this.page.set(1);
  }

  protected onPageSizeChange(value: string): void {
    const parsed = Number(value);
    const nextSize = this.pageSizes.find((size) => size === parsed) ?? this.pageSizes[0];
    this.pageSize.set(nextSize);
    this.page.set(1);
  }

  protected exportFilteredRows(): void {
    const rows = this.filteredRows();
    if (!rows.length) {
      alert('No rows available to export.');
      return;
    }

    const header = [
      'Bill ID',
      'Application No',
      'LOA ID',
      'Contract ID',
      'Bill Cycle',
      'Buyer',
      'Period',
      'Scheduled kWh',
      'RLDC Actual kWh',
      'Rate',
      'Net Payable',
      'Approval Status',
      'Status',
    ];

    const csvRows = rows.map((row) => [
      row.billId || 'Not generated',
      row.applicationNo,
      row.loiId,
      row.contractId,
      row.billCycle,
      row.buyer,
      row.period,
      String(row.scheduled),
      row.rldcActual === null ? '-' : String(row.rldcActual),
      String(row.rate),
      String(row.netPayable),
      row.approvalStatus,
      row.status,
    ]);

    const csv = [header, ...csvRows]
      .map((line) => line.map((value) => `"${String(value || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${this.mode().toUpperCase()}-billing-export-${this.formatDateCompact(new Date())}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  protected isSelected(rowKey: string): boolean {
    return !!this.selectedRows()[rowKey];
  }

  protected toggleSelection(rowKey: string, checked: boolean): void {
    this.selectedRows.update((current) => ({ ...current, [rowKey]: checked }));
  }

  protected mergeSelectedBills(): void {
    const selected = this.filteredRows().filter((row) => this.isSelected(row.rowKey));
    if (selected.length < 2) {
      alert('Select at least 2 bills to merge.');
      return;
    }

    const first = selected[0];
    const sameParties = selected.every((row) => row.seller === first.seller && row.buyer === first.buyer);
    if (!sameParties) {
      alert('Merge bill is allowed only when selected bills have same Seller and Buyer.');
      return;
    }

    const commonBillId = first.billId || this.nextBillId(first.mode);
    const updates: Record<string, string> = {};

    const finalized = selected.map((row) => {
      if (!row.billId && !row.canGenerate) return null;
      updates[row.rowKey] = commonBillId;
      return { ...row, billId: commonBillId, status: 'Generated' as BillStatus };
    });

    if (finalized.some((row) => !row)) {
      alert('Some selected rows are not eligible for bill generation.');
      return;
    }

    const rows = finalized.filter((row): row is BillRow => !!row);
    if (Object.keys(updates).length) {
      this.generatedBillIds.update((current) => ({ ...current, ...updates }));
      this.selectedRows.update((current) => {
        const next = { ...current };
        Object.keys(updates).forEach((rowKey) => {
          next[rowKey] = false;
        });
        return next;
      });
      this.persistGeneratedInvoices(
        rows.map((row) => ({
          row_key: row.rowKey,
          bill_id: row.billId,
          mode: row.mode,
          invoice_type: this.resolveInvoiceTypeFromTab(),
          bill_for: 'BUYER',
          row_json: this.buildPersistedRowJson(row, row.billId),
        })),
      );
    }

    this.downloadMergedInvoice(rows);
  }

  private buildRowsForMode(
    mode: BillMode,
    app: ApplicationApi,
    contract: Contract | undefined,
    sellerName: string,
    buyerName: string,
    buyerAddress: string,
    applicationNumbers: string[],
    loas: Map<string, Loa>,
    billConfigs: Record<string, LoaBillConfig[]>,
    generatedBillIds: Record<string, string>,
    yesterday: Date,
    today: Date,
  ): BillRow[] {
    const billType = mode === 'sop' ? 'SOP' : 'TMR';
    const buyerLoaId = this.resolveBuyerLoaId(app, contract);
    const sellerLoaId = this.resolveSellerLoaId(app, contract);
    const resolved = this.pickBillConfig([buyerLoaId, sellerLoaId].filter(Boolean), billType, billConfigs);
    if (!resolved) return [];

    const approvalStatus = app.approval_status || (app.approval_available ? 'Available' : 'Approval Not Available');
    const punchedSchedules = app.schedules || [];
    const approvalSchedules = this.toApprovalWindowSchedules(app.approval_schedules || []);
    const activeSchedules = approvalSchedules.length ? approvalSchedules : punchedSchedules;
    if (!punchedSchedules.length && !approvalSchedules.length) return [];

    const cycle = this.normalizeCycle(resolved.config.bill_cycle);
    const buyerLoaLabel = buyerLoaId ? (loas.get(buyerLoaId)?.loa_number || buyerLoaId) : '';
    const loiLabel = buyerLoaLabel || loas.get(resolved.loaId)?.loa_number || resolved.loaId;
    const billCycle = (resolved.config.bill_cycle || '').toString().trim() || 'NA';

    if (cycle === 'weekly') {
      const weeklyRows = this.buildWeeklyRowsForMode(
        mode,
        app,
        contract,
        sellerName,
        buyerName,
        buyerAddress,
        applicationNumbers,
        resolved.config,
        loiLabel,
        billCycle,
        punchedSchedules,
        approvalSchedules,
        approvalStatus,
        generatedBillIds,
      );
      if (weeklyRows.length) return weeklyRows;
    }

    if (cycle === 'daily' || cycle === 'days') {
      const dailyRows = this.buildDailyRowsForMode(
        mode,
        app,
        contract,
        sellerName,
        buyerName,
        buyerAddress,
        applicationNumbers,
        resolved.config,
        loiLabel,
        billCycle,
        punchedSchedules,
        approvalSchedules,
        approvalStatus,
        generatedBillIds,
      );
      if (dailyRows.length) return dailyRows;
    }

    const billWindow = this.resolveBillWindow(resolved.config, yesterday);
    const billWindowSchedules = this.clipSchedulesToRange(
      this.pickSchedulesForBillWindow(resolved.config, activeSchedules, yesterday),
      billWindow.start,
      billWindow.end,
    );
    const punchedScheduledQuantum = this.sumPunchedScheduleQuantum(
      this.clipSchedulesToRange(punchedSchedules, billWindow.start, billWindow.end),
    );
    const { billedQuantity, rate, netPayable } = this.calculateBillAmounts(
      mode,
      resolved.config,
      approvalSchedules,
      activeSchedules,
      billWindowSchedules,
      Number(app.approved_quantum) || Number(app.total_quantum) || 0,
      billWindow,
    );
    const rowKey = `${app.id}::${mode}`;
    const generatedBillId = generatedBillIds[rowKey] || '';

    return [
      {
        rowKey,
        applicationId: String(app.id || ''),
        applicationNo: app.application_number || String(app.id || '-'),
        applicationLink: app.id ? `/applications/${String(app.id)}` : '-',
        applicationCount: applicationNumbers.length,
        applicationNumbers,
        billId: generatedBillId,
        loiId: loiLabel,
        contractId: contract?.contract_number || app.contract_id,
        loiRef: loiLabel,
        sellerVendorId: String(app.seller_vendor_id || ''),
        seller: sellerName,
        buyer: buyerName,
        buyerAddress,
        period: this.billCyclePeriodLabel(resolved.config, yesterday),
        periodFrom: this.formatDisplayDate(billWindow.start),
        periodTo: this.formatDisplayDate(billWindow.end),
        billCycle,
        scheduled: punchedScheduledQuantum,
        billedQuantity,
        rldcActual: this.resolveRldcActualMwh(app, billWindow.start, billWindow.end),
        rate,
        netPayable,
        status: generatedBillId ? 'Generated' : 'Pending',
        mode,
        canGenerate: !!app.approval_available && billedQuantity > 0,
        approvalStatus,
        approvalAvailable: !!app.approval_available,
        scheduledTooltipLines: this.buildScheduledTooltipLines(
          this.clipSchedulesToRange(punchedSchedules, billWindow.start, billWindow.end),
        ),
        rldcTooltipLines: this.buildRldcTooltipLines(app, billWindow.start, billWindow.end),
        calculationTooltipLines: this.buildCalculationTooltipLines(billWindowSchedules),
      },
    ];
  }

  private buildWeeklyRowsForMode(
    mode: BillMode,
    app: ApplicationApi,
    contract: Contract | undefined,
    sellerName: string,
    buyerName: string,
    buyerAddress: string,
    applicationNumbers: string[],
    config: LoaBillConfig,
    loiLabel: string,
    billCycle: string,
    punchedSchedules: ApplicationScheduleApi[],
    approvalSchedules: ApplicationScheduleApi[],
    approvalStatus: string,
    generatedBillIds: Record<string, string>,
  ): BillRow[] {
    const punchedBuckets = this.scheduleSlicesByWeeklyBucket(punchedSchedules);
    const approvalBuckets = this.scheduleSlicesByWeeklyBucket(approvalSchedules);
    const allBucketKeys = Array.from(new Set([...punchedBuckets.keys(), ...approvalBuckets.keys()])).sort();
    if (!allBucketKeys.length) return [];

    return allBucketKeys.map((bucketKey) => {
      const punchedBucketItems = punchedBuckets.get(bucketKey) || [];
      const approvalBucketItems = approvalBuckets.get(bucketKey) || [];
      const bucketRange = this.weeklyBucketRangeFromKey(bucketKey);
      const fallbackApprovedBucketItems =
        !approvalBucketItems.length && !!app.approval_available
          ? this.applyQuantumOverride(
              punchedBucketItems,
              Number(app.approved_quantum) || Number(app.total_quantum) || 0,
            )
          : [];
      const billableBucketItems = approvalBucketItems.length ? approvalBucketItems : fallbackApprovedBucketItems;
      const generatedBillId = generatedBillIds[`${app.id}::${mode}::${bucketKey}`] || '';
      const scheduled = this.sumPunchedScheduleQuantum(punchedBucketItems);
      const { billedQuantity, rate, netPayable } = this.calculateBillAmounts(
        mode,
        config,
        approvalBucketItems.length ? approvalBucketItems : fallbackApprovedBucketItems,
        approvalBucketItems.length ? approvalBucketItems : punchedBucketItems,
        billableBucketItems,
        Number(app.approved_quantum) || Number(app.total_quantum) || 0,
        bucketRange,
      );
      return {
        rowKey: `${app.id}::${mode}::${bucketKey}`,
        groupKey: bucketKey,
        applicationId: String(app.id || ''),
        applicationNo: app.application_number || String(app.id || '-'),
        applicationLink: app.id ? `/applications/${String(app.id)}` : '-',
        applicationCount: applicationNumbers.length,
        applicationNumbers,
        billId: generatedBillId,
        loiId: loiLabel,
        contractId: contract?.contract_number || app.contract_id,
        loiRef: loiLabel,
        sellerVendorId: String(app.seller_vendor_id || ''),
        seller: sellerName,
        buyer: buyerName,
        buyerAddress,
        period: `${this.formatDisplayDate(bucketRange.start)} - ${this.formatDisplayDate(bucketRange.end)}`,
        periodFrom: this.formatDisplayDate(bucketRange.start),
        periodTo: this.formatDisplayDate(bucketRange.end),
        billCycle,
        scheduled,
        billedQuantity,
        rldcActual: this.resolveRldcActualMwh(app, bucketRange.start, bucketRange.end),
        rate,
        netPayable,
        status: generatedBillId ? 'Generated' : 'Pending',
        mode,
        canGenerate: !!app.approval_available && billedQuantity > 0,
        approvalStatus,
        approvalAvailable: !!app.approval_available,
        scheduledTooltipLines: this.buildScheduledTooltipLines(punchedBucketItems),
        rldcTooltipLines: this.buildRldcTooltipLines(app, bucketRange.start, bucketRange.end),
        calculationTooltipLines: this.buildCalculationTooltipLines(billableBucketItems),
      };
    });
  }

  private buildDailyRowsForMode(
    mode: BillMode,
    app: ApplicationApi,
    contract: Contract | undefined,
    sellerName: string,
    buyerName: string,
    buyerAddress: string,
    applicationNumbers: string[],
    config: LoaBillConfig,
    loiLabel: string,
    billCycle: string,
    punchedSchedules: ApplicationScheduleApi[],
    approvalSchedules: ApplicationScheduleApi[],
    approvalStatus: string,
    generatedBillIds: Record<string, string>,
  ): BillRow[] {
    const punchedByDay = this.dailyScheduleSlicesByDay(punchedSchedules);
    const approvalByDay = this.dailyScheduleSlicesByDay(approvalSchedules);
    const allDays = Array.from(new Set([...punchedByDay.keys(), ...approvalByDay.keys()])).sort();
    if (!allDays.length) return [];

    return allDays.map((dayKey) => {
      const day = this.parseDate(dayKey) || new Date(`${dayKey}T00:00:00`);
      const punchedDayItems = punchedByDay.get(dayKey) || [];
      const approvalDayItems = approvalByDay.get(dayKey) || [];
      const fallbackApprovedDayItems =
        !approvalDayItems.length && !!app.approval_available
          ? this.applyQuantumOverride(
              punchedDayItems,
              Number(app.approved_quantum) || Number(app.total_quantum) || 0,
            )
          : [];
      const billableDayItems = approvalDayItems.length ? approvalDayItems : fallbackApprovedDayItems;
      const isoDay = this.formatIsoDate(day);
      const rowKey = `${app.id}::${mode}::${isoDay}`;
      const generatedBillId = generatedBillIds[rowKey] || '';
      const scheduled = this.sumPunchedScheduleQuantum(punchedDayItems);
      const { billedQuantity, rate, netPayable } = this.calculateBillAmounts(
        mode,
        config,
        approvalDayItems.length ? approvalDayItems : fallbackApprovedDayItems,
        approvalDayItems.length ? approvalDayItems : punchedDayItems,
        billableDayItems,
        Number(app.approved_quantum) || Number(app.total_quantum) || 0,
        { start: day, end: day },
      );
      return {
        rowKey,
        applicationId: String(app.id || ''),
        applicationNo: app.application_number || String(app.id || '-'),
        applicationLink: app.id ? `/applications/${String(app.id)}` : '-',
        applicationCount: applicationNumbers.length,
        applicationNumbers,
        billId: generatedBillId,
        loiId: loiLabel,
        contractId: contract?.contract_number || app.contract_id,
        loiRef: loiLabel,
        sellerVendorId: String(app.seller_vendor_id || ''),
        seller: sellerName,
        buyer: buyerName,
        buyerAddress,
        period: `${this.formatDisplayDate(day)} - ${this.formatDisplayDate(day)}`,
        periodFrom: this.formatDisplayDate(day),
        periodTo: this.formatDisplayDate(day),
        billCycle,
        scheduled,
        billedQuantity,
        rldcActual: this.resolveRldcActualMwh(app, day, day),
        rate,
        netPayable,
        status: generatedBillId ? 'Generated' : 'Pending',
        mode,
        canGenerate: !!app.approval_available && billedQuantity > 0,
        approvalStatus,
        approvalAvailable: !!app.approval_available,
        scheduledTooltipLines: this.buildScheduledTooltipLines(punchedDayItems),
        rldcTooltipLines: this.buildRldcTooltipLines(app, day, day),
        calculationTooltipLines: this.buildCalculationTooltipLines(billableDayItems),
      };
    });
  }

  private applyQuantumOverride(schedules: ApplicationScheduleApi[], quantum: number): ApplicationScheduleApi[] {
    if (!schedules.length || quantum <= 0) return [];
    return schedules.map((schedule) => ({
      ...schedule,
      quantum,
    }));
  }

  private nextBillId(mode: BillMode): string {
    const serial = this.generatedSequence(mode) + 1;
    return this.buildBillId(mode, serial);
  }

  private buildBillId(mode: BillMode, serial: number): string {
    const prefix = mode === 'sop' ? 'SOP' : 'TMR';
    const ymd = this.formatDateCompact(new Date());
    return `${prefix}-BILL-${ymd}-${String(serial).padStart(4, '0')}`;
  }

  private generatedSequence(mode: BillMode): number {
    const prefix = mode === 'sop' ? 'SOP-BILL-' : 'TMR-BILL-';
    return Object.values(this.generatedBillIds()).filter((billId) => billId.startsWith(prefix)).length;
  }

  private persistGeneratedInvoices(
    items: Array<{
      row_key: string;
      bill_id: string;
      mode: string;
      invoice_type: string;
      bill_for: string;
      row_json: Record<string, unknown>;
    }>,
  ): void {
    if (!items.length) return;
    this.api.post<{ message: string }>('/api/invoices/generated', items).subscribe({
      error: () => {},
    });
  }

  private buildPersistedRowJson(row: BillRow, billId: string): Record<string, unknown> {
    return {
      row_key: row.rowKey,
      bill_id: billId,
      seller: row.seller,
      buyer: row.buyer,
      buyer_address: row.buyerAddress,
      application_id: row.applicationId,
      application_no: row.applicationNo,
      application_link: row.applicationLink,
      period: row.period,
      period_from: row.periodFrom,
      period_to: row.periodTo,
      bill_cycle: row.billCycle,
      scheduled: row.scheduled,
      billed_quantity: row.billedQuantity,
      rldc_actual: row.rldcActual,
      rate: row.rate,
      net_payable: row.netPayable,
      mode: row.mode,
      status: 'Generated',
    };
  }

  private resolveInvoiceTypeFromTab(): string {
    const tab = this.mode();
    if (tab === 'sop') return 'SOP';
    if (tab === 'tmr') return 'TMR';
    return 'OTHER';
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private downloadInvoice(row: BillRow): void {
    const html = this.buildInvoiceHtml(row);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    this.downloadBlob(blob, `${row.billId || 'invoice'}.html`);
  }

  private downloadMergedInvoice(rows: BillRow[]): void {
    const html = this.buildMergedInvoiceHtml(rows);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    this.downloadBlob(blob, `MERGED-BILL-${this.formatDateCompact(new Date())}.html`);
  }

  private buildMergedInvoiceHtml(rows: BillRow[]): string {
    const total = rows.reduce((sum, row) => sum + row.netPayable, 0);
    return this.buildBillDocumentHtml({
      title: 'Bill of Supply',
      billNo: `MERGED-${this.formatDateCompact(new Date())}`,
      billingDate: this.formatShortMonthDate(new Date()),
      dueDate: this.formatNumericDate(this.addDays(new Date(), 7)),
      financialYear: this.currentFinancialYear(new Date()),
      fromDate: this.periodStartDateLabel(rows[0] || null),
      toDate: this.periodEndDateLabel(rows[rows.length - 1] || null),
      sellerName: rows[0]?.seller || '-',
      sellerAddress: '-',
      buyerName: rows[0]?.buyer || '-',
      buyerAddress: rows[0]?.buyerAddress || '-',
      buyerPan: '-',
      lines: rows.map((row) => this.invoiceLineForRow(row)),
      totalAmount: total,
    });
  }

  private buildInvoiceHtml(row: BillRow): string {
    return this.buildBillDocumentHtml({
      title: 'Bill of Supply',
      billNo: row.billId || this.nextBillId(row.mode),
      billingDate: this.formatShortMonthDate(new Date()),
      dueDate: this.formatNumericDate(this.addDays(new Date(), 7)),
      financialYear: this.currentFinancialYear(new Date()),
      fromDate: this.periodStartDateLabel(row),
      toDate: this.periodEndDateLabel(row),
      sellerName: row.seller,
      sellerAddress: '-',
      buyerName: row.buyer,
      buyerAddress: row.buyerAddress,
      buyerPan: '-',
      lines: [this.invoiceLineForRow(row)],
      totalAmount: row.netPayable,
    });
  }

  private invoiceLineForRow(row: BillRow): InvoiceLineItem {
    return {
      description: `${row.mode === 'sop' ? 'POWER SUPPLY' : 'TRADING MARGIN'} FOR THE PERIOD ${row.period}`,
      hsnSac: '27160000',
      quantity: `${row.billedQuantity.toFixed(2)}`,
      rate: row.rate.toFixed(4),
      per: 'Units',
      amount: row.netPayable,
    };
  }

  private buildBillDocumentHtml(context: {
    title: string;
    billNo: string;
    billingDate: string;
    dueDate: string;
    financialYear: string;
    fromDate: string;
    toDate: string;
    sellerName: string;
    sellerAddress: string;
    buyerName: string;
    buyerAddress: string;
    buyerPan: string;
    lines: InvoiceLineItem[];
    totalAmount: number;
  }): string {
    const lineRows = context.lines
      .map(
        (line, index) => `<tr class="align-top">
          <td>${index + 1}</td>
          <td colspan="5">${this.escapeHtml(line.description)}</td>
          <td>${this.escapeHtml(line.hsnSac)}</td>
          <td>${this.escapeHtml(line.quantity)}</td>
          <td>${this.escapeHtml(line.rate)}</td>
          <td>${this.escapeHtml(line.per)}</td>
          <td colspan="2">Rs. ${line.amount.toFixed(2)}</td>
        </tr>`,
      )
      .join('');

    const primaryHsn = context.lines[0]?.hsnSac || '27160000';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${this.escapeHtml(context.billNo)} - Bill of Supply</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 18px; color: #111827; }
    .pdf-page { max-width: 960px; margin: 0 auto; }
    .pdf-hidden { display: block; }
    .heading { text-align: center; font-size: 18px; margin: 0 0 8px; font-weight: 700; }
    table { width: 100%; border-collapse: collapse; }
    .info-table td, .info-table th { border: 1px solid #2f3542; padding: 6px 7px; font-size: 12px; vertical-align: top; }
    .info-table th { font-weight: 700; }
    .nested-table { width: 100%; border-collapse: collapse; }
    .nested-table td { border: 1px solid #2f3542; padding: 6px; font-size: 11px; vertical-align: top; width: 50%; }
    .nested-table th { display:none; }
    .text-center { text-align: center; }
    .text-end { text-align: right; }
    .align-top { vertical-align: top; }
    .amount-words td { font-weight: 700; }
    .float-right { float: right; }
    .logo-mark { text-align: center; font-size: 11px; font-weight: 700; line-height: 1.2; }
    .border { border: 1px solid #2f3542; }
    .signature-box { min-height: 64px; }
    .bold { font-weight: 700; }
    .fw-bold { font-weight: 700; }
    .fw-normal { font-weight: 400; }
    .small { font-size: 11px; line-height: 1.45; }
    .mt-2 { margin-top: 10px; }
    .pt-2 { padding-top: 8px; }
    .my-2 { margin-top: 8px; margin-bottom: 8px; }
    .ms-auto { margin-left: auto; }
    .p-1 { padding: 4px; }
    .p-2 { padding: 8px; }
    .border-top { border-top: 1px solid #2f3542; }
    .col-5 { width: 280px; margin-left: auto; }
    .offset-7 { margin-left: auto; }
  </style>
</head>
<body>
  <div class="pdf-page pdf-hidden" id="pdfContent">
    <div id="formatD" class="pdf-page">
      <h5 class="heading">${this.escapeHtml(context.title)}</h5>
      <table class="info-table">
        <colgroup>
          <col style="width: 5.5%" />
          <col style="width: 5.5%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 8%" />
          <col style="width: 11%" />
          <col style="width: 11%" />
          <col style="width: 11%" />
        </colgroup>
        <tr>
          <td colspan="2" class="w-10 align-middle text-center">
            <div class="logo-mark">TRADE<br/>YOUR AMP</div>
          </td>
          <td colspan="7" class="w-40 align-top p-2">
            <strong>Address</strong><br>
            ${this.escapeHtml(this.issuerProfile.companyName)}<br>
            ${this.escapeHtml(this.issuerProfile.address)}<br>
            <strong>PAN:</strong> ${this.escapeHtml(this.issuerProfile.pan)}<br>
            <strong>GSTIN/UIN:</strong> ${this.escapeHtml(this.issuerProfile.gst)}<br>
            <strong>State:</strong> ${this.escapeHtml(this.issuerProfile.state)} <strong>Code:</strong> ${this.escapeHtml(this.issuerProfile.stateCode)}<br>
            <strong>CIN:</strong> ${this.escapeHtml(this.issuerProfile.cin)}<br>
          </td>
          <td colspan="3" class="p-1">
            <table class="nested-table">
              <tr>
                <th scope="row">Invoice and Billing Date</th>
                <td><span class="bold">Invoice</span><br>${this.escapeHtml(context.billNo)}</td>
                <td><span class="bold">Billing Date</span><br>${this.escapeHtml(context.billingDate)}</td>
              </tr>
              <tr>
                <th scope="row">Due Date and Financial Year</th>
                <td><span class="bold">Due Date</span><br>${this.escapeHtml(context.dueDate)}</td>
                <td><span class="bold">Financial Year</span><br>${this.escapeHtml(context.financialYear)}</td>
              </tr>
              <tr>
                <th scope="row">From Date and To Date</th>
                <td><span class="bold">From Date</span><br>${this.escapeHtml(context.fromDate)}</td>
                <td><span class="bold">To Date</span><br>${this.escapeHtml(context.toDate)}</td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td colspan="9" class="align-top p-2">
            <strong>Consignee (Ship To)</strong><br>
            ${this.escapeHtml(context.sellerName)}<br>
            ${this.escapeHtml(context.sellerAddress)}<br>
            <strong>GSTIN:</strong> -<br>
            <strong>PAN:</strong> -<br>
            <strong>State:</strong> - <strong>Code:</strong> -<br>
            <strong>Place of Supply:</strong> -
          </td>
          <td colspan="3" class="align-top p-2">
            <strong>Buyer (Ship To)</strong><br>
            ${this.escapeHtml(context.buyerName)}<br>
            ${this.escapeHtml(context.buyerAddress)}<br>
            <strong>GSTIN:</strong> -<br>
            <strong>PAN:</strong> ${this.escapeHtml(context.buyerPan)}<br>
            <strong>State:</strong> - <strong>Code:</strong> -<br>
            <strong>Place of Supply:</strong> -
          </td>
        </tr>
        <tr class="align-top text-center">
          <th id="sn">S.No.</th>
          <th colspan="5" id="partyname">Description</th>
          <th id="hsnsac">HSN/SAC</th>
          <th id="partyquant">Quantity</th>
          <th id="rate">Rate</th>
          <th id="per">Per</th>
          <th colspan="2" class="text-end" id="partyamt">Amount</th>
        </tr>
        ${lineRows}
        <tr>
          <td colspan="10"><strong>Total</strong></td>
          <td colspan="2"><strong>Rs. ${context.totalAmount.toFixed(2)}</strong></td>
        </tr>
        <tr class="amount-words">
          <td colspan="12">
            <strong>Amount Chargeable (in words)</strong>
            <span class="float-right">E. & O.E</span>
          </td>
        </tr>
        <tr>
          <td colspan="12">INR ${this.escapeHtml(this.numberToWordsIndian(Math.round(context.totalAmount)))} Rupees Only</td>
        </tr>
        <tr>
          <th id="hsn" colspan="8">HSN/SAC</th>
          <th id="taxable" colspan="4">Taxable Value</th>
        </tr>
        <tr>
          <td id="hsn" colspan="8">${this.escapeHtml(primaryHsn)}</td>
          <td id="taxable" colspan="4">${context.totalAmount.toFixed(2)}</td>
        </tr>
        <tr>
          <td id="hsn" colspan="8">Total</td>
          <td id="taxable" colspan="4">Rs. ${context.totalAmount.toFixed(2)}</td>
        </tr>
        <tr>
          <td colspan="8">
            <strong>Total Amount (in Words):</strong> INR ${this.escapeHtml(this.numberToWordsIndian(Math.round(context.totalAmount)))} Rupees Only<br>
            <strong>Company's PAN:</strong> ${this.escapeHtml(context.buyerPan)}
          </td>
          <td colspan="4" rowspan="2">
            <strong>Company's Bank Details</strong><br>
            <b>A/c Holder's Name</b> : ${this.escapeHtml(this.bankProfile.accountHolderName)}<br>
            <b>Bank Name</b> : ${this.escapeHtml(this.bankProfile.bankName)}<br>
            <b>A/c No.</b> : ${this.escapeHtml(this.bankProfile.accountNumber)}<br>
            <b>Branch & IFSC Code</b> : ${this.escapeHtml(this.bankProfile.branch)} & ${this.escapeHtml(this.bankProfile.ifscCode)}<br>
            <b>SWIFT Code</b> : ${this.escapeHtml(this.bankProfile.swiftCode)}
          </td>
        </tr>
        <tr>
          <td colspan="8">
            <strong>Declaration</strong><br>
            We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
          </td>
        </tr>
      </table>
      <div class="mt-2">
        <div class="col-5 offset-7 border">
          <div class="fw-bold text-center">${this.escapeHtml(this.issuerProfile.companyName)}</div>
          <div class="signature-box my-2 ms-auto"></div>
          <div class="fw-bold text-center">Authorised Signatory</div>
        </div>
      </div>
      <div class="row border-top mt-2 pt-2 text-center small fw-bold">
        <div>Corporate Address : ${this.escapeHtml(this.issuerProfile.address)}</div>
        <div class="fw-normal">Registered Address : ${this.escapeHtml(this.issuerProfile.address)}</div>
        <div class="fw-normal">CIN: ${this.escapeHtml(this.issuerProfile.cin)} | PAN: ${this.escapeHtml(this.issuerProfile.pan)} | GSTIN: ${this.escapeHtml(this.issuerProfile.gst)}</div>
      </div>
    </div>
  </div>
</body>
</html>`;
  }

  private resolveSellerLoaId(app: ApplicationApi, contract: Contract | undefined): string {
    if (app.seller_loa_id) return app.seller_loa_id;
    if (!contract?.party_details?.length) return '';

    const targetVendorId = app.seller_vendor_id || '';
    if (targetVendorId) {
      const matched = contract.party_details.find((row) => (row.vendor_id || '') === targetVendorId && !!row.loa_id);
      if (matched?.loa_id) return matched.loa_id;
    }

    return contract.party_details.find((row) => !!row.loa_id)?.loa_id || '';
  }

  private resolveBuyerVendorId(
    app: ApplicationApi,
    contract: Contract | undefined,
    loas: Map<string, Loa>,
  ): string {
    if (app.buyer_vendor_id) return app.buyer_vendor_id;
    if (!contract?.party_details?.length) return '';

    const buyerParty =
      contract.party_details.find((row) => this.resolveContractPartyRole(row.loa_id, row.party_type, loas) === 'buyer')
      || contract.party_details.find((row) => (row.vendor_id || '') !== (app.seller_vendor_id || ''))
      || null;

    return String(buyerParty?.vendor_id || '').trim();
  }

  private resolveBuyerLoaId(app: ApplicationApi, contract: Contract | undefined): string {
    if (app.buyer_loa_id) return app.buyer_loa_id;
    if (!contract?.party_details?.length) return '';

    const targetVendorId = app.buyer_vendor_id || '';
    if (targetVendorId) {
      const matched = contract.party_details.find((row) => (row.vendor_id || '') === targetVendorId && !!row.loa_id);
      if (matched?.loa_id) return matched.loa_id;
    }

    const loas = new Map(this.loas().map((item) => [String(item.id), item]));
    const buyerParty =
      contract.party_details.find((row) => this.resolveContractPartyRole(row.loa_id, row.party_type, loas) === 'buyer')
      || contract.party_details.find((row) => (row.vendor_id || '') !== (app.seller_vendor_id || '') && !!row.loa_id)
      || null;

    return String(buyerParty?.loa_id || '').trim();
  }

  private resolveContractPartyRole(
    loaId: string | null | undefined,
    partyType: string | null | undefined,
    loas: Map<string, Loa>,
  ): 'buyer' | 'seller' | 'bank' | 'unknown' {
    const loa = loas.get(String(loaId || '').trim());
    const loaKind = this.resolveLoaKind(loa);
    if (loaKind === 'sell') return 'seller';
    if (loaKind === 'buy') return 'buyer';
    if (loaKind === 'bank') return 'bank';

    const normalizedPartyType = String(partyType || '').trim().toLowerCase();
    if (normalizedPartyType.includes('sell')) return 'seller';
    if (normalizedPartyType.includes('buy')) return 'buyer';
    if (normalizedPartyType.includes('bank')) return 'bank';
    return 'unknown';
  }

  private resolveLoaKind(loa: Loa | null | undefined): 'buy' | 'sell' | 'bank' | 'unknown' {
    const category = String(loa?.category || '').trim().toLowerCase();
    if (category.includes('sell') || category.includes('sale')) return 'sell';
    if (category.includes('buy') || category.includes('purchase')) return 'buy';
    if (category.includes('bank')) return 'bank';
    return 'unknown';
  }

  private pickBillConfig(
    loaIds: string[],
    billType: 'SOP' | 'TMR',
    billConfigsByLoa: Record<string, LoaBillConfig[]>,
  ): { loaId: string; config: LoaBillConfig } | null {
    for (const loaId of loaIds) {
      const matched = (billConfigsByLoa[loaId] || [])
        .filter((item) => (item.bill_type || '').toUpperCase() === billType)
        .sort((a, b) => (b.effective_date || '').localeCompare(a.effective_date || ''));
      if (matched.length > 0) return { loaId, config: matched[0] };
    }
    return null;
  }

  private shouldGenerateBill(config: LoaBillConfig, schedules: ApplicationScheduleApi[], yesterday: Date, today: Date): boolean {
    const cycle = this.normalizeCycle(config.bill_cycle);
    if (cycle === 'daily' || cycle === 'days') {
      return schedules.some((item) => this.scheduleCoversDate(item, yesterday));
    }

    if (!this.isCycleDueToday(cycle, config, today, yesterday)) return false;
    return this.pickSchedulesForBillWindow(config, schedules, yesterday).length > 0;
  }

  private pickSchedulesForBillWindow(config: LoaBillConfig, schedules: ApplicationScheduleApi[], yesterday: Date): ApplicationScheduleApi[] {
    const cycle = this.normalizeCycle(config.bill_cycle);

    if (cycle === 'daily' || cycle === 'days') {
      return schedules.filter((item) => this.scheduleCoversDate(item, yesterday));
    }

    const window = this.resolveBillWindow(config, yesterday);
    return schedules.filter((item) => this.scheduleOverlapsRange(item, window.start, window.end));
  }

  private resolveBillWindow(config: LoaBillConfig, yesterday: Date): { start: Date; end: Date } {
    const cycle = this.normalizeCycle(config.bill_cycle);
    if (cycle === 'weekly') {
      return this.weeklyWindowRange(yesterday);
    }
    if (cycle === 'monthly') {
      const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), 1);
      return {
        start,
        end: this.lastDayOfMonth(yesterday),
      };
    }
    return {
      start: this.billWindowStart(config, yesterday),
      end: this.startOfDay(yesterday),
    };
  }

  private isCycleDueToday(cycle: string, config: LoaBillConfig, today: Date, yesterday: Date): boolean {
    if (cycle === 'weekly') return this.daysSinceAnchor(config, today) % 7 === 0;
    if (cycle === 'fortnightly') return this.daysSinceAnchor(config, today) % 14 === 0;
    if (cycle === 'monthly') {
      const anchor = this.anchorDate(config, today);
      const wantedDay = Math.min(anchor.getDate(), this.lastDayOfMonth(yesterday).getDate());
      return yesterday.getDate() === wantedDay;
    }
    if (cycle.startsWith('custom:')) return this.isCustomCycleCutoffDay(cycle, yesterday);
    return false;
  }

  private billWindowStart(config: LoaBillConfig, yesterday: Date): Date {
    const cycle = this.normalizeCycle(config.bill_cycle);
    if (cycle === 'weekly') return this.weeklyWindowRange(yesterday).start;
    if (cycle === 'fortnightly') return this.addDays(yesterday, -13);
    if (cycle === 'monthly') return new Date(yesterday.getFullYear(), yesterday.getMonth(), 1);
    if (cycle.startsWith('custom:')) {
      const range = this.matchCustomRangeForDate(cycle, yesterday);
      if (!range) return new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
      const start = Math.max(1, Math.min(range.start, this.lastDayOfMonth(yesterday).getDate()));
      return new Date(yesterday.getFullYear(), yesterday.getMonth(), start);
    }
    return new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  }

  private weeklyWindowRange(yesterday: Date): { start: Date; end: Date } {
    const day = yesterday.getDate();
    const monthEnd = this.lastDayOfMonth(yesterday).getDate();
    if (day >= 29) {
      return {
        start: new Date(yesterday.getFullYear(), yesterday.getMonth(), 29),
        end: new Date(yesterday.getFullYear(), yesterday.getMonth(), monthEnd),
      };
    }
    if (day >= 22) {
      return {
        start: new Date(yesterday.getFullYear(), yesterday.getMonth(), 22),
        end: new Date(yesterday.getFullYear(), yesterday.getMonth(), 28),
      };
    }
    if (day >= 15) {
      return {
        start: new Date(yesterday.getFullYear(), yesterday.getMonth(), 15),
        end: new Date(yesterday.getFullYear(), yesterday.getMonth(), 21),
      };
    }
    if (day >= 8) {
      return {
        start: new Date(yesterday.getFullYear(), yesterday.getMonth(), 8),
        end: new Date(yesterday.getFullYear(), yesterday.getMonth(), 14),
      };
    }
    return {
      start: new Date(yesterday.getFullYear(), yesterday.getMonth(), 1),
      end: new Date(yesterday.getFullYear(), yesterday.getMonth(), 7),
    };
  }

  private isCustomCycleCutoffDay(cycle: string, yesterday: Date): boolean {
    const range = this.matchCustomRangeForDate(cycle, yesterday);
    if (!range) return false;
    const monthEnd = this.lastDayOfMonth(yesterday).getDate();
    return yesterday.getDate() === Math.min(range.end, monthEnd);
  }

  private matchCustomRangeForDate(cycle: string, date: Date): { start: number; end: number } | null {
    const ranges = this.parseCustomRanges(cycle);
    if (!ranges.length) return null;
    const day = date.getDate();
    return ranges.find((item) => day >= item.start && day <= item.end) || null;
  }

  private parseCustomRanges(cycle: string): Array<{ start: number; end: number }> {
    const raw = cycle.replace(/^custom:/i, '').trim();
    if (!raw) return [];

    return raw
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const parts = item.split('-').map((part) => part.trim());
        if (parts.length !== 2) return null;
        const start = Number(parts[0]);
        const end = /^last\s*day$/i.test(parts[1]) ? 31 : Number(parts[1]);
        if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end < start) return null;
        return { start, end };
      })
      .filter((item): item is { start: number; end: number } => !!item);
  }

  private daysSinceAnchor(config: LoaBillConfig, today: Date): number {
    const anchor = this.anchorDate(config, today);
    const diffMs = this.startOfDay(today).getTime() - this.startOfDay(anchor).getTime();
    return Math.floor(diffMs / 86400000);
  }

  private anchorDate(config: LoaBillConfig, fallback: Date): Date {
    if (config.effective_date) {
      const parsed = new Date(`${config.effective_date}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return fallback;
  }

  private normalizeCycle(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private resolveRate(mode: BillMode, config: LoaBillConfig, schedules: ApplicationScheduleApi[]): number {
    if (mode === 'tmr') return this.toNumber(config.tmr_rate) || this.toNumber(config.rate);
    if ((config.rate_type || '').trim().toUpperCase() !== 'MCP') {
      return this.toNumber(config.rate) || this.toNumber(config.mcp_rate);
    }
    return this.resolveSopMcpRate(config, schedules);
  }

  private resolveSopMcpRate(config: LoaBillConfig, schedules: ApplicationScheduleApi[]): number {
    const condition = this.normalizeMcpCondition(config.mcp_condition);
    const exactRows = this.pickExactMcpRows(schedules);
    const averageRows = this.pickAverageMcpRows(schedules);
    const base = this.averageMcpValue(condition === 'average' ? averageRows : exactRows);
    const offset = this.toNumber(config.mcp_rate) || this.toNumber(config.rate);

    if (condition === 'less') return Math.max(base - offset, 0);
    if (condition === 'add') return base + offset;
    if (condition === 'equal' || condition === 'average') return base;
    return base || this.toNumber(config.rate) || this.toNumber(config.mcp_rate);
  }

  private normalizeMcpCondition(value: string | null | undefined): 'average' | 'equal' | 'less' | 'add' | '' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'average') return 'average';
    if (normalized === 'equal' || normalized === 'equal to') return 'equal';
    if (normalized === 'less' || normalized === 'less than') return 'less';
    if (normalized === 'add' || normalized === 'greater than') return 'add';
    return '';
  }

  private toApprovalWindowSchedules(schedules: ApplicationApprovalScheduleApi[]): ApplicationScheduleApi[] {
    return (schedules || [])
      .filter((item) => item.is_approved !== false)
      .map((item) => ({
        from_date: item.schedule_from,
        to_date: item.schedule_to || item.schedule_from,
        from_time: item.start_time || '00:00',
        to_time: item.end_time || '24:00',
        quantum: Number(item.accepted_mw ?? item.requested_mw ?? item.mwh) || 0,
      }));
  }

  private pickAverageMcpRows(schedules: ApplicationScheduleApi[]): McpReportRowApi[] {
    const rows = this.latestMcpRows();
    if (!rows.length || !schedules.length) return [];
    const fromDates = schedules.map((item) => item.from_date).filter(Boolean).sort();
    const toDates = schedules.map((item) => item.to_date || item.from_date).filter(Boolean).sort();
    const minDate = fromDates[0] || '';
    const maxDate = toDates[toDates.length - 1] || minDate;
    return rows.filter((row) => row.report_date >= minDate && row.report_date <= maxDate && this.toNumber(row.mcp) > 0);
  }

  private pickExactMcpRows(schedules: ApplicationScheduleApi[]): McpReportRowApi[] {
    const rows = this.latestMcpRows();
    if (!rows.length || !schedules.length) return [];
    return rows.filter((row) => {
      const period = Number(row.period);
      return schedules.some((schedule) => {
        if (row.report_date < schedule.from_date || row.report_date > (schedule.to_date || schedule.from_date)) return false;
        if (!Number.isFinite(period)) return true;
        const startBlock = this.timeToBlock(schedule.from_time || '00:00');
        const endBlock = this.timeToBlock(schedule.to_time || '24:00');
        return period >= startBlock && period <= Math.max(startBlock, endBlock);
      });
    });
  }

  private averageMcpValue(rows: McpReportRowApi[]): number {
    const values = rows.map((item) => this.toNumber(item.mcp)).filter((item) => item > 0);
    if (!values.length) return 0;
    return values.reduce((sum, item) => sum + item, 0) / values.length;
  }

  private timeToBlock(value: string): number {
    const [hourRaw, minuteRaw] = (value || '00:00').split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 1;
    if (hour >= 24) return 96;
    return Math.max(1, Math.min(96, Math.floor((hour * 60 + minute) / 15) + 1));
  }

  private toNumber(value: string | number | null | undefined): number {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }

  private sumPunchedScheduleQuantum(schedules: ApplicationScheduleApi[], fallback = 0): number {
    const total = schedules.reduce((sum, row) => sum + this.scheduleBillQuantum(row), 0);
    return total > 0 ? total : fallback;
  }

  private sumScheduleBillQuantum(schedules: ApplicationScheduleApi[], fallback = 0): number {
    const total = schedules.reduce((sum, row) => sum + this.scheduleBillQuantum(row), 0);
    return total > 0 ? total : fallback;
  }

  private scheduleSlicesByWeeklyBucket(schedules: ApplicationScheduleApi[]): Map<string, ApplicationScheduleApi[]> {
    const grouped = new Map<string, ApplicationScheduleApi[]>();
    this.expandSchedulesToDailySlices(schedules).forEach(({ day, schedules: daySchedules }) => {
      const key = this.weeklyBucketKey(day);
      const existing = grouped.get(key) || [];
      existing.push(...daySchedules);
      grouped.set(key, existing);
    });
    return grouped;
  }

  private weeklyBucketKey(day: Date): string {
    const range = this.weeklyBucketRangeForDate(day);
    return `${this.formatIsoDate(range.start)}::${this.formatIsoDate(range.end)}`;
  }

  private weeklyBucketRangeFromKey(key: string): { start: Date; end: Date } {
    const [startRaw, endRaw] = key.split('::');
    const start = this.parseDate(startRaw) || new Date(`${startRaw}T00:00:00`);
    const end = this.parseDate(endRaw) || new Date(`${endRaw}T00:00:00`);
    return { start: this.startOfDay(start), end: this.startOfDay(end) };
  }

  private weeklyBucketRangeForDate(day: Date): { start: Date; end: Date } {
    const target = this.startOfDay(day);
    const dayOfMonth = target.getDate();
    const monthEnd = this.lastDayOfMonth(target).getDate();
    if (dayOfMonth >= 24) {
      return {
        start: new Date(target.getFullYear(), target.getMonth(), 24),
        end: new Date(target.getFullYear(), target.getMonth(), monthEnd),
      };
    }
    if (dayOfMonth >= 16) {
      return {
        start: new Date(target.getFullYear(), target.getMonth(), 16),
        end: new Date(target.getFullYear(), target.getMonth(), 23),
      };
    }
    if (dayOfMonth >= 9) {
      return {
        start: new Date(target.getFullYear(), target.getMonth(), 9),
        end: new Date(target.getFullYear(), target.getMonth(), 15),
      };
    }
    return {
      start: new Date(target.getFullYear(), target.getMonth(), 1),
      end: new Date(target.getFullYear(), target.getMonth(), 8),
    };
  }

  private periodRangeFromSchedules(
    schedules: ApplicationScheduleApi[],
    fallback: { start: Date; end: Date },
  ): { start: Date; end: Date } {
    if (!schedules.length) return fallback;
    const parsedRanges = schedules
      .map((schedule) => ({
        start: this.parseDate(schedule.from_date),
        end: this.parseDate(schedule.to_date || schedule.from_date),
      }))
      .filter((item): item is { start: Date; end: Date } => !!item.start && !!item.end)
      .sort((left, right) => left.start.getTime() - right.start.getTime());
    if (!parsedRanges.length) return fallback;
    const minStart = parsedRanges[0].start;
    const maxEnd = parsedRanges.reduce((max, item) => (item.end > max ? item.end : max), parsedRanges[0].end);
    return { start: minStart, end: maxEnd };
  }

  private mergeDisplayPeriod(leftPeriod: string, rightPeriod: string): string {
    const left = this.periodDateRange(leftPeriod);
    const right = this.periodDateRange(rightPeriod);
    const leftStart = this.parsePeriodDisplayDate(left.from);
    const leftEnd = this.parsePeriodDisplayDate(left.to);
    const rightStart = this.parsePeriodDisplayDate(right.from);
    const rightEnd = this.parsePeriodDisplayDate(right.to);
    const starts = [leftStart, rightStart].filter((value): value is Date => !!value);
    const ends = [leftEnd, rightEnd].filter((value): value is Date => !!value);
    if (!starts.length || !ends.length) return leftPeriod || rightPeriod;
    const start = starts.reduce((min, value) => (value < min ? value : min), starts[0]);
    const end = ends.reduce((max, value) => (value > max ? value : max), ends[0]);
    return `${this.formatDisplayDate(start)} - ${this.formatDisplayDate(end)}`;
  }

  private calculateBillAmounts(
    mode: BillMode,
    config: LoaBillConfig,
    approvalSchedules: ApplicationScheduleApi[],
    activeSchedules: ApplicationScheduleApi[],
    billableSchedules: ApplicationScheduleApi[],
    approvedQuantumFallback: number,
    window: { start: Date; end: Date },
  ): { approvedQuantum: number; billedQuantity: number; rate: number; netPayable: number } {
    const approvedQuantum = this.sumPunchedScheduleQuantum(approvalSchedules, approvedQuantumFallback);
    const billedQuantity = this.resolveBilledQuantity(billableSchedules, activeSchedules, approvedQuantum, window);
    const rate = this.resolveRate(mode, config, billableSchedules.length ? billableSchedules : activeSchedules);
    return {
      approvedQuantum,
      billedQuantity,
      rate,
      netPayable: billedQuantity * rate,
    };
  }

  private resolveRldcActualMwh(app: ApplicationApi, start: Date, end: Date): number | null {
    if (!app.approval_available) return null;
    const approvedQuantum = Number(app.approved_quantum) || 0;
    if (approvedQuantum <= 0) return null;

    const approvedSchedules = (app.approval_schedules || [])
      .filter((item) => item.is_approved === true)
      .filter((item) => {
        const from = this.parseDate(item.schedule_from);
        const to = this.parseDate(item.schedule_to || item.schedule_from);
        if (!from || !to) return false;
        return from <= end && to >= start;
      })
      .map((item): ApplicationScheduleApi => ({
        from_date: item.schedule_from,
        to_date: item.schedule_to || item.schedule_from,
        from_time: item.start_time || '00:00',
        to_time: item.end_time || '24:00',
        quantum: approvedQuantum,
      }));

    const clippedApprovedSchedules = this.clipSchedulesToRange(approvedSchedules, start, end);
    const approvalMwh = this.sumScheduleBillQuantum(clippedApprovedSchedules, 0);
    if (approvalMwh > 0) return approvalMwh;

    const syntheticSchedule: ApplicationScheduleApi = {
      from_date: this.formatIsoDate(start),
      to_date: this.formatIsoDate(end),
      from_time: '00:00',
      to_time: '24:00',
      quantum: approvedQuantum,
    };
    return this.sumScheduleBillQuantum([syntheticSchedule], 0);
  }

  private buildRldcTooltipLines(app: ApplicationApi, start: Date, end: Date): string[] {
    if (!app.approval_available) {
      return ['Approval not available, so RLDC Approval is not calculated'];
    }

    const approvedQuantum = Number(app.approved_quantum) || 0;
    if (approvedQuantum <= 0) {
      return ['Approved quantum is missing, so RLDC Approval is not calculated'];
    }

    const overlappingSchedules = (app.approval_schedules || [])
      .filter((item) => item.is_approved === true)
      .filter((item) => {
        const from = this.parseDate(item.schedule_from);
        const to = this.parseDate(item.schedule_to || item.schedule_from);
        if (!from || !to) return false;
        return from <= end && to >= start;
      })
      .map((item): ApplicationScheduleApi => ({
        from_date: item.schedule_from,
        to_date: item.schedule_to || item.schedule_from,
        from_time: item.start_time || '00:00',
        to_time: item.end_time || '24:00',
        quantum: approvedQuantum,
      }));

    const clippedSchedules = this.clipSchedulesToRange(overlappingSchedules, start, end);
    const lines: string[] = [
      'RLDC Approval = day(s) x hour(s) x approved quantum x 250',
      `Approved Quantum: ${this.formatMoney(approvedQuantum)}`,
    ];

    if (clippedSchedules.length) {
      lines.push('Source: Approved approval schedules overlapping this bill period');
      clippedSchedules.slice(0, 3).forEach((schedule, index) => {
        const days = this.scheduleDayDifference(schedule);
        const hours = this.scheduleHourDifference(schedule.from_time, schedule.to_time);
        const total = this.scheduleBillQuantum(schedule);
        lines.push(
          `Schedule ${index + 1}: ${days} day(s) x ${hours.toFixed(2)} hr x ${this.formatMoney(approvedQuantum)} x 250 = ${this.formatMoney(total)} kWh`,
        );
      });
      if (clippedSchedules.length > 3) {
        lines.push(`+${clippedSchedules.length - 3} more approved schedule(s)`);
      }
      lines.push(`Total RLDC Approval: ${this.formatMoney(this.sumScheduleBillQuantum(clippedSchedules, 0))} kWh`);
      return lines;
    }

    const syntheticSchedule: ApplicationScheduleApi = {
      from_date: this.formatIsoDate(start),
      to_date: this.formatIsoDate(end),
      from_time: '00:00',
      to_time: '24:00',
      quantum: approvedQuantum,
    };
    const days = this.scheduleDayDifference(syntheticSchedule);
    const hours = this.scheduleHourDifference(syntheticSchedule.from_time, syntheticSchedule.to_time);
    const total = this.scheduleBillQuantum(syntheticSchedule);
    lines.push('Source: Fallback full-period calculation because no approved schedule rows were found in this period');
    lines.push(
      `Fallback: ${days} day(s) x ${hours.toFixed(2)} hr x ${this.formatMoney(approvedQuantum)} x 250 = ${this.formatMoney(total)} kWh`,
    );
    lines.push(`Total RLDC Approval: ${this.formatMoney(total)} kWh`);
    return lines;
  }

  private resolveBilledQuantity(
    preferredSchedules: ApplicationScheduleApi[],
    fallbackSchedules: ApplicationScheduleApi[],
    approvedQuantum: number,
    window: { start: Date; end: Date },
  ): number {
    const directQuantity = this.sumScheduleBillQuantum(preferredSchedules);
    if (directQuantity > 0) return directQuantity;
    if (approvedQuantum <= 0) return 0;

    const timingSource = preferredSchedules.length ? preferredSchedules : fallbackSchedules;
    if (timingSource.length) {
      const approvedSchedules = this.applyQuantumOverride(timingSource, approvedQuantum);
      const scheduleQuantity = this.sumScheduleBillQuantum(approvedSchedules, 0);
      if (scheduleQuantity > 0) return scheduleQuantity;
    }

    const syntheticSchedule: ApplicationScheduleApi = {
      from_date: this.formatIsoDate(window.start),
      to_date: this.formatIsoDate(window.end),
      from_time: '00:00',
      to_time: '24:00',
      quantum: approvedQuantum,
    };
    return this.sumScheduleBillQuantum([syntheticSchedule], 0);
  }

  private clipSchedulesToRange(schedules: ApplicationScheduleApi[], start: Date, end: Date): ApplicationScheduleApi[] {
    return schedules
      .map((schedule) => this.clipScheduleToRange(schedule, start, end))
      .filter((schedule): schedule is ApplicationScheduleApi => !!schedule);
  }

  private expandSchedulesToDailySlices(
    schedules: ApplicationScheduleApi[],
  ): Array<{ day: Date; schedules: ApplicationScheduleApi[] }> {
    const grouped = new Map<string, ApplicationScheduleApi[]>();

    schedules.forEach((schedule) => {
      const from = this.parseDate(schedule.from_date);
      const to = this.parseDate(schedule.to_date || schedule.from_date);
      if (!from || !to || from > to) return;

      for (let cursor = from; cursor <= to; cursor = this.addDays(cursor, 1)) {
        const day = this.startOfDay(cursor);
        const clipped = this.clipScheduleToRange(schedule, day, day);
        if (!clipped) continue;
        const key = this.formatIsoDate(day);
        const existing = grouped.get(key) || [];
        existing.push(clipped);
        grouped.set(key, existing);
      }
    });

    return Array.from(grouped.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([isoDay, daySchedules]) => ({
        day: this.parseDate(isoDay) || new Date(`${isoDay}T00:00:00`),
        schedules: daySchedules,
      }));
  }

  private dailyScheduleSlicesByDay(schedules: ApplicationScheduleApi[]): Map<string, ApplicationScheduleApi[]> {
    return new Map(this.expandSchedulesToDailySlices(schedules).map(({ day, schedules: daySchedules }) => [this.formatIsoDate(day), daySchedules]));
  }

  private clipScheduleToRange(schedule: ApplicationScheduleApi, start: Date, end: Date): ApplicationScheduleApi | null {
    const from = this.parseDate(schedule.from_date);
    const to = this.parseDate(schedule.to_date || schedule.from_date);
    if (!from || !to) return null;

    const overlapStart = from > start ? from : start;
    const overlapEnd = to < end ? to : end;
    if (overlapStart > overlapEnd) return null;

    return {
      ...schedule,
      from_date: this.formatIsoDate(overlapStart),
      to_date: this.formatIsoDate(overlapEnd),
      from_time: this.isSameDay(overlapStart, from) ? schedule.from_time : '00:00',
      to_time: this.isSameDay(overlapEnd, to) ? schedule.to_time : '24:00',
    };
  }

  private scheduleBillQuantum(schedule: ApplicationScheduleApi): number {
    const quantum = Number(schedule.quantum) || 0;
    if (quantum <= 0) return 0;

    const dayDifference = this.scheduleDayDifference(schedule);
    if (dayDifference <= 0) return 0;
    const timeDifference = this.scheduleHourDifference(schedule.from_time, schedule.to_time);
    if (timeDifference <= 0) return 0;

    return dayDifference * timeDifference * quantum * 250;
  }

  private buildCalculationTooltipLines(schedules: ApplicationScheduleApi[]): string[] {
    if (!schedules.length) return [];

    const lines = schedules.slice(0, 3).map((schedule, index) => {
      const dayDifference = this.scheduleDayDifference(schedule);
      const hours = this.scheduleHourDifference(schedule.from_time, schedule.to_time);
      const quantum = Number(schedule.quantum) || 0;
      return `Schedule ${index + 1}: ${dayDifference} day(s), ${hours.toFixed(2)} hr, quantum ${this.formatMoney(quantum)}`;
    });

    if (schedules.length > 3) {
      lines.push(`+${schedules.length - 3} more schedule(s)`);
    }

    return lines;
  }

  private buildScheduledTooltipLines(schedules: ApplicationScheduleApi[]): string[] {
    if (!schedules.length) return ['No punched schedules found for this period'];

    const lines = schedules.slice(0, 3).map((schedule, index) => {
      const from = this.parseDate(schedule.from_date);
      const to = this.parseDate(schedule.to_date || schedule.from_date);
      const period =
        from && to
          ? `${this.formatDisplayDate(from)} - ${this.formatDisplayDate(to)}`
          : `${schedule.from_date || '-'} - ${schedule.to_date || schedule.from_date || '-'}`;
      const fromTime = (schedule.from_time || '00:00').trim() || '00:00';
      const toTime = (schedule.to_time || '24:00').trim() || '24:00';
      const quantum = Number(schedule.quantum) || 0;
      const total = this.scheduleBillQuantum(schedule);

      return `Schedule ${index + 1}: Period ${period}, Time ${fromTime} - ${toTime}, Quantum ${this.formatMoney(quantum)}, Total ${this.formatMoney(total)} kWh`;
    });

    if (schedules.length > 3) {
      lines.push(`+${schedules.length - 3} more schedule(s)`);
    }

    return lines;
  }

  private billCyclePeriodLabel(config: LoaBillConfig, yesterday: Date): string {
    const window = this.resolveBillWindow(config, yesterday);
    return `${this.formatDisplayDate(window.start)} - ${this.formatDisplayDate(window.end)}`;
  }

  private scheduleDayDifference(schedule: ApplicationScheduleApi): number {
    const from = this.parseDate(schedule.from_date);
    const to = this.parseDate(schedule.to_date || schedule.from_date);
    if (!from || !to) return 0;
    return Math.max(1, Math.floor((this.startOfDay(to).getTime() - this.startOfDay(from).getTime()) / 86400000) + 1);
  }

  private scheduleHourDifference(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const startMinutes = this.timeToMinutes(fromTime, false);
    const endMinutes = this.timeToMinutes(toTime, true);
    if (startMinutes === null || endMinutes === null) return 0;
    if (endMinutes <= startMinutes) return 0;
    return (endMinutes - startMinutes) / 60;
  }

  private timeToMinutes(value: string | null | undefined, allow24Hour = false): number | null {
    const raw = (value || '').trim();
    if (!raw) return allow24Hour ? 1440 : 0;
    const [hourRaw, minuteRaw] = raw.split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    if (allow24Hour && hour === 24 && minute === 0) return 1440;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return (hour * 60) + minute;
  }

  private scheduleCoversDate(schedule: ApplicationScheduleApi, date: Date): boolean {
    const from = this.parseDate(schedule.from_date);
    const to = this.parseDate(schedule.to_date);
    if (!from || !to) return false;
    const target = this.startOfDay(date);
    return target >= from && target <= to;
  }

  private scheduleOverlapsRange(schedule: ApplicationScheduleApi, start: Date, end: Date): boolean {
    const from = this.parseDate(schedule.from_date);
    const to = this.parseDate(schedule.to_date);
    if (!from || !to) return false;
    return from <= end && to >= start;
  }

  private periodLabel(schedules: ApplicationScheduleApi[]): string {
    if (!schedules.length) return '-';
    const sorted = [...schedules].sort((a, b) => (a.from_date || '').localeCompare(b.from_date || ''));
    const from = this.parseDate(sorted[0].from_date);
    const to = this.parseDate(sorted[sorted.length - 1].to_date);
    if (!from || !to) return '-';
    const fmt = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${fmt.format(from)} - ${fmt.format(to)}`;
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const raw = value.trim();
    if (!raw) return null;

    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (ymd) {
      const parsed = new Date(`${raw}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) return this.startOfDay(parsed);
    }

    const dmy = /^(\d{2})[/-](\d{2})[/-](\d{4})$/.exec(raw);
    if (dmy) {
      const day = Number(dmy[1]);
      const month = Number(dmy[2]);
      const year = Number(dmy[3]);
      if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
      const parsed = new Date(year, month - 1, day);
      if (!Number.isNaN(parsed.getTime())) return this.startOfDay(parsed);
    }

    const parsed = new Date(`${raw}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return null;
    return this.startOfDay(parsed);
  }

  private parseIsoDate(value: string | null | undefined): Date | null {
    return this.parseDate(value);
  }

  private parsePeriodDisplayDate(value: string | null | undefined): Date | null {
    if (!value || value === '-') return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return this.startOfDay(parsed);
  }

  private startOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private addDays(value: Date, days: number): Date {
    const result = this.startOfDay(value);
    result.setDate(result.getDate() + days);
    return result;
  }

  private lastDayOfMonth(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth() + 1, 0);
  }

  private formatDisplayDate(value: Date): string {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(value);
  }

  private formatShortMonthDate(value: Date): string {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).format(value);
  }

  private formatNumericDate(value: Date): string {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = String(value.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }

  private formatDateCompact(value: Date): string {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  private formatIsoDate(value: Date): string {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private currentFinancialYear(value: Date): string {
    const year = value.getFullYear();
    const month = value.getMonth();
    const startYear = month >= 3 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${String(endYear).slice(-2)}`;
  }

  private periodStartDateLabel(row: BillRow | null): string {
    if (!row) return '-';
    return row.periodFrom || this.periodDateRange(row.period || '').from;
  }

  private periodEndDateLabel(row: BillRow | null): string {
    if (!row) return '-';
    return row.periodTo || this.periodDateRange(row.period || '').to;
  }

  private periodDateRange(period: string): { from: string; to: string } {
    const separator = ' - ';
    const raw = (period || '').trim();
    const separatorIndex = raw.indexOf(separator);
    if (separatorIndex >= 0) {
      return {
        from: raw.slice(0, separatorIndex).trim(),
        to: raw.slice(separatorIndex + separator.length).trim(),
      };
    }
    return { from: raw || '-', to: raw || '-' };
  }

  private isSameDay(left: Date, right: Date): boolean {
    return (
      left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
    );
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private numberToWordsIndian(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return 'Zero';

    const ones = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const belowThousand = (num: number): string => {
      const hundred = Math.floor(num / 100);
      const rem = num % 100;
      const head = hundred ? `${ones[hundred]} Hundred` : '';
      if (!rem) return head || 'Zero';
      if (rem < 20) return `${head} ${ones[rem]}`.trim();
      return `${head} ${tens[Math.floor(rem / 10)]} ${ones[rem % 10]}`.trim();
    };

    const crore = Math.floor(value / 10000000);
    const lakh = Math.floor((value % 10000000) / 100000);
    const thousand = Math.floor((value % 100000) / 1000);
    const rest = value % 1000;

    const parts: string[] = [];
    if (crore) parts.push(`${belowThousand(crore)} Crore`);
    if (lakh) parts.push(`${belowThousand(lakh)} Lakh`);
    if (thousand) parts.push(`${belowThousand(thousand)} Thousand`);
    if (rest) parts.push(belowThousand(rest));
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }
}
